import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FolderIcon from '@mui/icons-material/Folder';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {
  SyncAdminService,
  SyncDeviceTokenRegenerated,
  SyncDeviceView,
} from '../../../../services/openapi';
import NewDeviceDialog from './NewDeviceDialog';
import MappingsDialog from './MappingsDialog';

interface IDeviceStatus {
  queue_length?: number;
  last_error?: string | null;
  paused?: boolean;
  mappings?: { localPath: string; s3Prefix: string; enabled: boolean }[];
}

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

function parseStatus(device: SyncDeviceView): IDeviceStatus {
  if (!device.last_status) return {};
  try {
    return JSON.parse(device.last_status);
  } catch {
    return {};
  }
}

function isOnline(device: SyncDeviceView): boolean {
  if (!device.last_seen) return false;
  // last_seen is stored as naive UTC
  return Date.now() - new Date(device.last_seen + 'Z').getTime() < ONLINE_THRESHOLD_MS;
}

function formatLastSeen(device: SyncDeviceView): string {
  if (!device.last_seen) return 'noch nie';
  return new Date(device.last_seen + 'Z').toLocaleString('de-DE');
}

export default function SyncGeraete() {
  const { call_backend_api } = React.useContext(APIWrapperContext);
  const [devices, setDevices] = React.useState<SyncDeviceView[]>([]);
  const [showNewDialog, setShowNewDialog] = React.useState(false);
  const [deviceToDelete, setDeviceToDelete] = React.useState<SyncDeviceView | null>(null);
  const [deviceForMappings, setDeviceForMappings] = React.useState<SyncDeviceView | null>(null);
  const [deviceToRegenerate, setDeviceToRegenerate] = React.useState<SyncDeviceView | null>(null);
  const [regeneratedToken, setRegeneratedToken] = React.useState('');

  const loadDevices = React.useCallback(() => {
    call_backend_api(
      SyncAdminService.listSyncDevicesSyncAdminDevicesGet(),
      (res: SyncDeviceView[]) => setDevices(res),
      undefined,
      undefined,
      false
    );
  }, [call_backend_api]);

  React.useEffect(() => {
    loadDevices();
    const interval = setInterval(loadDevices, 30000);
    return () => clearInterval(interval);
  }, [loadDevices]);

  const handleToggleEnabled = (device: SyncDeviceView) => {
    call_backend_api(
      SyncAdminService.updateSyncDeviceSyncAdminDevicesDeviceIdPatch(device.id, {
        enabled: !device.enabled,
      }),
      () => loadDevices(),
      undefined,
      undefined,
      true,
      device.enabled ? 'Gerät deaktiviert' : 'Gerät aktiviert',
      'Fehler beim Aktualisieren des Geräts!'
    );
  };

  const handleDelete = () => {
    if (!deviceToDelete) return;
    call_backend_api(
      SyncAdminService.deleteSyncDeviceSyncAdminDevicesDeviceIdDelete(deviceToDelete.id),
      () => {
        setDeviceToDelete(null);
        loadDevices();
      },
      undefined,
      undefined,
      true,
      'Gerät wurde gelöscht',
      'Fehler beim Löschen des Geräts!'
    );
  };

  const handleRegenerate = () => {
    if (!deviceToRegenerate) return;
    call_backend_api(
      SyncAdminService.regenerateSyncDeviceTokenSyncAdminDevicesDeviceIdRegeneratePost(
        deviceToRegenerate.id
      ),
      (res: SyncDeviceTokenRegenerated) => {
        setRegeneratedToken(res.token);
        loadDevices();
      },
      undefined,
      undefined,
      true,
      'Token wurde neu generiert',
      'Fehler beim Generieren des Tokens!'
    );
  };

  const closeRegenerateDialog = () => {
    setDeviceToRegenerate(null);
    setRegeneratedToken('');
  };

  return (
    <Container component="main">
      <Typography sx={{ maxWidth: '100%', typography: { xs: 'h4', md: 'h3' } }}>
        Sync-Geräte
      </Typography>
      <Typography sx={{ my: 2 }} color="text.secondary">
        Kunden-PCs mit installierter Sync-App, die lokale Ordner in die Dokumentenablage
        synchronisieren. Der Geräte-Token wird bei der Einrichtung der Sync-App eingetragen.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setShowNewDialog(true)}>
          Neues Gerät
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Zuletzt gesehen</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Token</TableCell>
              <TableCell>Ordner</TableCell>
              <TableCell>Warteschlange</TableCell>
              <TableCell>Aktiviert</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Noch keine Sync-Geräte angelegt.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {devices.map((device) => {
              const status = parseStatus(device);
              const online = isOnline(device);
              return (
                <TableRow key={device.id}>
                  <TableCell>{device.name}</TableCell>
                  <TableCell>
                    {status.last_error ? (
                      <Tooltip title={status.last_error}>
                        <Chip label="Fehler" color="error" size="small" />
                      </Tooltip>
                    ) : status.paused ? (
                      <Chip label="Pausiert" color="warning" size="small" />
                    ) : (
                      <Chip
                        label={online ? 'Online' : 'Offline'}
                        color={online ? 'success' : 'default'}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatLastSeen(device)}</TableCell>
                  <TableCell>{device.agent_version || '–'}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: 'monospace' }} variant="body2">
                      {device.token_prefix}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<FolderIcon />}
                      onClick={() => setDeviceForMappings(device)}>
                      {(() => {
                        try {
                          return JSON.parse(device.mappings || '[]').length;
                        } catch {
                          return 0;
                        }
                      })()}{' '}
                      bearbeiten
                    </Button>
                  </TableCell>
                  <TableCell>{status.queue_length ?? '–'}</TableCell>
                  <TableCell>
                    <Switch
                      checked={device.enabled}
                      onChange={() => handleToggleEnabled(device)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Token neu generieren">
                      <IconButton onClick={() => setDeviceToRegenerate(device)}>
                        <AutorenewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Gerät löschen">
                      <IconButton onClick={() => setDeviceToDelete(device)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <NewDeviceDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={loadDevices}
      />

      <MappingsDialog
        device={deviceForMappings}
        onClose={() => setDeviceForMappings(null)}
        onSaved={loadDevices}
      />

      <Dialog open={deviceToDelete !== null} onClose={() => setDeviceToDelete(null)}>
        <DialogTitle>Sync-Gerät löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Das Gerät "{deviceToDelete?.name}" kann sich danach nicht mehr synchronisieren. Die
            bereits synchronisierten Dateien bleiben erhalten.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceToDelete(null)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deviceToRegenerate !== null} onClose={closeRegenerateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Token neu generieren</DialogTitle>
        <DialogContent>
          {regeneratedToken === '' ? (
            <DialogContentText>
              Der bisherige Token von "{deviceToRegenerate?.name}" wird sofort ungültig. Die
              Sync-App auf dem Kunden-PC muss danach mit dem neuen Token neu verbunden werden.
            </DialogContentText>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Dieser Token wird nur einmal angezeigt.
              </Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                  {regeneratedToken}
                </Typography>
                <IconButton
                  onClick={() => navigator.clipboard.writeText(regeneratedToken)}
                  title="Token kopieren">
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {regeneratedToken === '' ? (
            <>
              <Button onClick={closeRegenerateDialog}>Abbrechen</Button>
              <Button color="warning" variant="contained" onClick={handleRegenerate}>
                Neu generieren
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={closeRegenerateDialog}>
              Fertig
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
